/******/ (() => { // webpackBootstrap
class SearchDropdown extends HTMLElement {
  constructor() {
    super();
    this.closeButton = this.querySelector('[data-search-dropdown-close]');
    this.initialFocus = this.querySelector('[data-search-dropdown-initial-focus]');
    this.triggerElement = null;

    this.setHeightVariables = this.setHeightVariables.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.openDropdown = this.openDropdown.bind(this);
    this.closeDropdown = this.closeDropdown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.activateOverlayHeader = this.activateOverlayHeader.bind(this);
    this.deactivateOverlayHeader = this.deactivateOverlayHeader.bind(this);
    this.handleFocusKeyDown = this.handleFocusKeyDown.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-search-dropdown'});
    this.triggers = Array.from(document.querySelectorAll('.search-button'));
    this.triggers.forEach(trigger => {
      trigger.setAttribute('aria-controls', this.id);
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', this.getAttribute('aria-hidden') === 'false' ? 'true' : 'false');
    });

    // Add event listeners
    window.eventBus.on('toggle:search:dropdown', this.toggleDropdown);
    window.eventBus.on('close:search:dropdown', this.closeDropdown);
    this.addEventListener('transitionend', this.handleTransitionEnd);
    this.closeButton.addEventListener('click', this.closeDropdown);

    // Set css variables for header and announcement bar heights for max dropdown height calc
    this.setHeightVariables();
    window.addEventListener('resize', this.setHeightVariables);
  }

  setHeightVariables() {
    const headerHeightDesktop = document.querySelector('.site-header')?.offsetHeight || 0;
    const headerHeightMobile = document.querySelector('.mobile-nav-bar-wrapper')?.offsetHeight || 0;
    this.style.setProperty('--header-height-desktop', `${headerHeightDesktop}px`);
    this.style.setProperty('--header-height-mobile', `${headerHeightMobile}px`);

    const announcementBarHeight = document.querySelector('.announcement_bar')?.offsetHeight || 0;
    this.style.setProperty('--announcement-bar-height', `${announcementBarHeight}px`);
    if (this.getAttribute('aria-hidden') === 'false') this.loadVisibleImages();
  }

  loadVisibleImages() {
    // The mobile empty state has links only; its desktop product grid stays inert.
    // Test the card's layout too: at tablet/desktop widths the fourth card is hidden.
    this.querySelectorAll('template[data-deferred-image]').forEach(template => {
      const card = template.closest('.predictive-search__grid-item');
      if (!card || card.getClientRects().length === 0) return;
      const content = template.content.cloneNode(true);
      content.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      template.replaceWith(content);
    });
  }

  toggleDropdown(e) {
    e.stopPropagation();
    if (this.getAttribute('aria-hidden') === 'true') {
      this.openDropdown();
    } else {
      this.closeDropdown();
    }
  }

  openDropdown() {
    this.triggerElement = document.activeElement;
    this.setAttribute('aria-hidden', 'false');
    this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'true'));
    this.loadVisibleImages();
    this.classList.add('shown-on-scroll');
    document.body.setAttribute('data-search-dropdown-open', 'true');
    document.body.setAttribute('data-search-dropdown-overlay-visible', 'true');
    this.activateOverlayHeader();
    document.addEventListener('click', this.handleClickOutside);
    this.querySelector('predictive-search')?.activateSearch();
    // Focus synchronously with the shopper's tap so mobile keyboards can open.
    this.initialFocus.focus({ preventScroll: true });
  }

  closeDropdown() {
    // Restore focus to the triggering element
    if (this.triggerElement) {
      this.triggerElement.focus({ preventScroll: true });
      this.triggerElement = null;
    }
    this.setAttribute('aria-hidden', 'true');
    this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
    this.classList.remove('shown-on-scroll');
    document.body.removeAttribute('data-search-dropdown-overlay-visible');
    document.removeEventListener('click', this.handleClickOutside);
  }

  trapFocus() {
    // Visibility transitions can reject the initial synchronous focus attempt.
    this.initialFocus.focus({ preventScroll: true });
    this.addEventListener('keydown', this.handleFocusKeyDown);
  }

  handleFocusKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeDropdown();
      return;
    }
    if (e.key === 'Tab') {
      const focusableElements = Array.from(this.querySelectorAll('button:not([disabled]), [href]:not([tabindex="-1"]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')).filter(element => !element.closest('[hidden]') && element.getClientRects().length > 0);
      if (focusableElements.length === 0) return;
      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    }
  }

  activateOverlayHeader() {
    const overlayHeaders = document.querySelectorAll('.overlay-header');
    if (overlayHeaders.length > 0) {
      overlayHeaders.forEach(header => {
        header.classList.add('overlay-header--hover');
      });
    }
  }

  deactivateOverlayHeader() {
    const overlayHeaders = document.querySelectorAll('.overlay-header');
    if (overlayHeaders.length > 0) {
      overlayHeaders.forEach(header => {
        header.classList.remove('overlay-header--hover');
      });
    }
  }

  handleTransitionEnd(e) {
    if (e.target !== this) return;

    if (this.getAttribute('aria-hidden') === 'true') {
      this.removeEventListener('keydown', this.handleFocusKeyDown);
      document.body.removeAttribute('data-search-dropdown-open');
      // After drawer has finished closing, remove hover state from overlay headers
      this.deactivateOverlayHeader();
    } else {
      // After drawer has finished opening, trap focus
      this.trapFocus();
    }
  }

  handleClickOutside(e) {
    // If clicking in search or dismissing popup, do nothing
    if (this.contains(e.target) || e.target.id === 'PopupOverlay' || e.target.closest('#popup')) return;
    this.closeDropdown();
  }

  disconnectedCallback() {
    // Remove event listeners
    window.eventBus.off('toggle:search:dropdown', this.toggleDropdown);
    window.eventBus.off('close:search:dropdown', this.closeDropdown);
    this.removeEventListener('transitionend', this.handleTransitionEnd);
    this.closeButton.removeEventListener('click', this.closeDropdown);
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.setHeightVariables);
    this.removeEventListener('keydown', this.handleFocusKeyDown);
    document.body.removeAttribute('data-search-dropdown-open');
    document.body.removeAttribute('data-search-dropdown-overlay-visible');
  }
}

customElements.define('search-dropdown', SearchDropdown);

/******/ })()
;
